import { useEffect, useRef, useState } from "react";
import { ChevronLeft, ChevronRight, ExternalLink, Flame } from "lucide-react";
import { localizedNews, timeAgo } from "../lib/api";
import { useI18n } from "../i18n";
import NewsPreview from "./NewsPreview";

const SWIPE_THRESHOLD = 45;
const FALLBACK_BACKGROUND = "https://images.unsplash.com/photo-1590283603385-17ffb3a7f29f?crop=entropy&cs=srgb&fm=jpg&w=1600&q=60";

export default function HotNewsHero({ items, loading }) {
  const { t, lang } = useI18n();
  const articles = (items || []).slice(0, 4);
  const [activeIndex, setActiveIndex] = useState(0);
  const [selected, setSelected] = useState(null);
  const touchStart = useRef(null);
  const carouselRef = useRef(null);

  const selectSlide = (index) => {
    if (!articles.length) return;
    setActiveIndex((index + articles.length) % articles.length);
  };
  const nextSlide = () => selectSlide(activeIndex + 1);
  const previousSlide = () => selectSlide(activeIndex - 1);

  useEffect(() => {
    setActiveIndex((index) => Math.min(index, Math.max(articles.length - 1, 0)));
  }, [articles.length]);

  const handleKeyDown = (event) => {
    if (event.key === "ArrowLeft") {
      event.preventDefault();
      previousSlide();
    } else if (event.key === "ArrowRight") {
      event.preventDefault();
      nextSlide();
    } else if ((event.key === "Enter" || event.key === " ") && event.currentTarget === carouselRef.current) {
      event.preventDefault();
      setSelected(articles[activeIndex]);
    }
  };

  const handleTouchStart = (event) => {
    touchStart.current = event.changedTouches[0]?.clientX ?? null;
  };
  const handleTouchEnd = (event) => {
    if (touchStart.current === null) return;
    const distance = (event.changedTouches[0]?.clientX ?? touchStart.current) - touchStart.current;
    touchStart.current = null;
    if (Math.abs(distance) < SWIPE_THRESHOLD) return;
    if (distance < 0) nextSlide();
    else previousSlide();
  };

  const localizedArticles = articles.map((item) => localizedNews(item, lang));
  const activeArticle = localizedArticles[activeIndex];

  return (
    <div
      ref={carouselRef}
      className="relative h-auto border border-border/60 rounded-sm bg-[#09090B]"
      data-testid="hot-news-hero"
      role="region"
      aria-roledescription="carousel"
      aria-label={t("hot_news")}
      tabIndex={-1}
      onKeyDown={handleKeyDown}
      onTouchStart={handleTouchStart}
      onTouchEnd={handleTouchEnd}
      style={{
        backgroundImage: `linear-gradient(rgba(5,5,5,0.85), rgba(5,5,5,0.95)), url('${FALLBACK_BACKGROUND}')`,
        backgroundSize: "cover",
        backgroundPosition: "center",
      }}
    >
      <div className="flex items-center gap-2 px-4 sm:px-6 pt-5 min-w-0">
        <div className="flex items-center gap-1.5 px-2 py-1 rounded-sm bg-red-500/15 border border-red-500/40">
          <Flame className="w-3 h-3 text-red-400" strokeWidth={2} />
          <span className="text-[10px] font-mono font-bold uppercase tracking-widest text-red-400">{t("hot_news")}</span>
        </div>
        <div className="text-[10px] font-mono text-zinc-500 uppercase tracking-widest truncate">{t("market_moving")}</div>
      </div>

      <div className="grid h-auto grid-cols-1 md:grid-cols-3 auto-rows-max items-start gap-4 p-4 sm:p-6" data-testid="hot-news-layout">
        {loading && (
          <>
            <div className="shimmer aspect-[3/2] md:aspect-[2.7/1] w-full rounded-sm md:col-span-2" data-testid="hot-news-loading-frame" data-aspect-ratio="mobile:3:2 desktop:2.7:1" />
            <div className="space-y-3 h-auto self-start">
              {[0, 1, 2].map((index) => (
                <div key={index} className="shimmer rounded-sm p-4">
                  <span className="block w-3/4 rounded-sm bg-white/10 py-1" />
                </div>
              ))}
            </div>
          </>
        )}

        {!loading && !articles.length && (
          <div className="md:col-span-3 p-8 text-center text-zinc-500 text-sm" data-testid="hot-news-building">
            {t("news_building")}
          </div>
        )}

        {!loading && articles.length > 0 && (
          <div className="md:col-span-2 h-auto self-start min-w-0" data-testid="hot-news-hero-column">
            <div className="relative aspect-[3/2] md:aspect-[2.7/1] w-full" data-testid="hot-news-image-frame" data-aspect-ratio="mobile:3:2 desktop:2.7:1">
              {localizedArticles.map((article, index) => {
                const isActive = index === activeIndex;
                return (
                  <article
                    key={article.id || `${article.url}-${index}`}
                    className={`absolute inset-0 rounded-sm border border-border/60 overflow-hidden ${isActive ? "flex" : "hidden"} flex-col justify-end`}
                    role="group"
                    aria-roledescription="slide"
                    aria-label={`${index + 1} ${t("of")} ${articles.length}: ${article.title}`}
                    aria-hidden={!isActive}
                    data-testid={`hot-news-slide-${index}`}
                    data-background-image={article.image || FALLBACK_BACKGROUND}
                    tabIndex={isActive ? 0 : -1}
                    onKeyDown={isActive ? handleKeyDown : undefined}
                  >
                    <img
                      src={article.image || FALLBACK_BACKGROUND}
                      alt=""
                      aria-hidden="true"
                      className="absolute inset-0 h-full w-full object-cover"
                      onError={(event) => {
                        if (event.currentTarget.src !== FALLBACK_BACKGROUND) event.currentTarget.src = FALLBACK_BACKGROUND;
                      }}
                      data-testid={`hot-news-slide-image-${index}`}
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-[rgba(5,5,5,0.97)] via-[rgba(5,5,5,0.48)] to-[rgba(5,5,5,0.08)]" aria-hidden="true" />
                    <div className="relative z-10 p-4 sm:p-6 pt-16 sm:pt-20">
                      <div className="flex items-center gap-2 mb-2 text-[10px] font-mono uppercase text-zinc-300 flex-wrap">
                        <span className="text-emerald-300">{article.source}</span>
                        <span>·</span>
                        <span>{timeAgo(article.published_at, lang)}</span>
                      </div>
                      <h3 className="font-display text-xl sm:text-2xl md:text-3xl font-black leading-tight text-white break-words line-clamp-3">{article.title}</h3>
                      {article.body?.trim() && <p className="text-sm text-zinc-300 mt-3 max-w-2xl leading-relaxed break-words line-clamp-3">{article.body}</p>}
                      <div className="flex flex-wrap items-center gap-2 mt-4">
                        <button
                          type="button"
                          onClick={() => setSelected(articles[index])}
                          className="min-h-10 px-3 py-2 rounded-sm border border-white/25 bg-black/30 text-xs font-mono uppercase tracking-widest text-white hover:bg-white/10"
                          data-testid="hot-news-preview-button"
                        >
                          {t("preview_story")}
                        </button>
                        {article.url && (
                          <a
                            href={article.url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-flex min-h-10 items-center gap-1.5 px-3 py-2 rounded-sm border border-emerald-500/50 bg-emerald-500/10 text-xs font-mono uppercase tracking-widest text-emerald-300 hover:bg-emerald-500/20"
                            data-testid="hot-news-source-link"
                          >
                            {t("read_source")} <ExternalLink className="w-3 h-3" strokeWidth={1.7} />
                          </a>
                        )}
                      </div>
                    </div>
                  </article>
                );
              })}
              <button type="button" onClick={previousSlide} aria-label={lang === "id" ? "Cerita sebelumnya" : "Previous story"} className="absolute left-3 top-1/2 -translate-y-1/2 z-20 inline-flex h-11 w-11 items-center justify-center rounded-full border border-white/30 bg-black/55 text-white hover:bg-black/80" data-testid="hot-news-previous">
                <ChevronLeft aria-hidden="true" className="h-5 w-5" />
              </button>
              <button type="button" onClick={nextSlide} aria-label={lang === "id" ? "Cerita berikutnya" : "Next story"} className="absolute right-3 top-1/2 -translate-y-1/2 z-20 inline-flex h-11 w-11 items-center justify-center rounded-full border border-white/30 bg-black/55 text-white hover:bg-black/80" data-testid="hot-news-next">
                <ChevronRight aria-hidden="true" className="h-5 w-5" />
              </button>
            </div>
          </div>
        )}

        {!loading && articles.length > 0 && (
          <div className="h-auto self-start space-y-3 min-w-0" aria-label={t("story_navigation")} data-testid="hot-news-article-list">
            {localizedArticles.map((article, index) => (
              <button key={article.id || `${article.url}-${index}-nav`} type="button" onClick={() => selectSlide(index)} aria-current={index === activeIndex ? "true" : undefined} className={`block w-full text-left border rounded-sm p-3 transition-colors duration-150 group ${index === activeIndex ? "border-emerald-400/70 bg-emerald-500/[0.08]" : "border-border/60 hover:bg-white/[0.03] hover:border-white/20"}`} data-testid={`hot-news-item-${article.id}`}>
                <div className="flex items-center gap-2 text-[10px] font-mono uppercase text-zinc-500 mb-1"><span className="text-emerald-400">{article.source}</span><span>·</span><span>{timeAgo(article.published_at, lang)}</span></div>
                <div className="text-sm text-white font-medium group-hover:text-emerald-300 leading-snug line-clamp-2">{article.title}</div>
              </button>
            ))}
          </div>
        )}
      </div>
      {!loading && articles.length > 0 && (
        <>
          <div className="flex items-center justify-center gap-2 px-4 sm:px-6 pb-4 sm:pb-6" role="tablist" aria-label={t("story_indicators")} data-testid="hot-news-pagination">
            {articles.map((article, index) => (
              <button key={article.id || `${article.url}-${index}-dot`} type="button" role="tab" aria-selected={index === activeIndex} aria-label={`${t("story")} ${index + 1} ${t("of")} ${articles.length}`} tabIndex={index === activeIndex ? 0 : -1} onClick={() => selectSlide(index)} className={`h-3 min-w-3 rounded-full border transition-colors ${index === activeIndex ? "w-7 border-emerald-300 bg-emerald-300" : "w-3 border-zinc-500 bg-zinc-700 hover:border-white"}`} data-testid={`hot-news-dot-${index}`} />
            ))}
          </div>
          <p className="sr-only" aria-live="polite" aria-atomic="true">
            {activeArticle ? `${t("story")} ${activeIndex + 1} ${t("of")} ${articles.length}: ${activeArticle.title}` : ""}
          </p>
        </>
      )}
      <NewsPreview open={!!selected} onOpenChange={(open) => !open && setSelected(null)} item={selected} />
    </div>
  );
}
